import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const DELIVERY_TYPE_LABELS = {
    pdf: "Archivo PDF",
    photo: "Foto",
    text: "Texto escrito",
};

export default function TareaEntregas() {
    const { assignmentId } = useParams();
    const navigate = useNavigate();
    const [assignment, setAssignment] = useState(null);
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [ar, sr] = await Promise.all([
                api.get(`/assignments/${assignmentId}`),
                api.get(`/assignments/${assignmentId}/submissions`),
            ]);
            setAssignment(ar.data);
            setSubs(sr.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error cargando la tarea");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [assignmentId]);

    if (loading) return <div className="p-10 text-edu-muted">Cargando…</div>;
    if (!assignment) return <div className="p-10 text-edu-muted">Tarea no encontrada.</div>;

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver
            </button>

            <div className="edu-card p-6 mb-6">
                <h1 className="text-2xl font-semibold">{assignment.title}</h1>
                {assignment.instructions && <p className="text-edu-body mt-2">{assignment.instructions}</p>}
                <div className="flex flex-wrap gap-4 mt-4 text-sm text-edu-muted">
                    <span>{DELIVERY_TYPE_LABELS[assignment.delivery_type] || assignment.delivery_type}</span>
                    <span>Puntaje máximo: {assignment.max_score} pts</span>
                    {assignment.due_date && <span>Entrega: {assignment.due_date}</span>}
                    <span className="inline-flex items-center gap-1"><Users size={13} /> {subs.length} entregado{subs.length !== 1 ? "s" : ""}</span>
                </div>
            </div>

            {subs.length === 0 ? (
                <div className="edu-card p-10 text-center text-edu-muted">Nadie ha entregado esta tarea todavía.</div>
            ) : (
                <div className="space-y-3">
                    {subs.map((s) => {
                        const isGraded = s.score !== null && s.score !== undefined;
                        const errorCount = s.ai_grammar_report?.error_count ?? null;
                        const criteriaCount = s.ai_rubric_report?.criterios?.length ?? 0;
                        return (
                            <button
                                key={s.id}
                                onClick={() => navigate(`/profesor/tareas/${assignmentId}/entregas/${s.id}`)}
                                className="edu-card w-full text-left p-5 hover:bg-edu-soft transition-colors flex items-center justify-between gap-4 flex-wrap"
                            >
                                <div>
                                    <p className="font-medium text-sm">{s.student_name}</p>
                                    <p className="text-xs text-edu-muted mt-0.5">{new Date(s.submitted_at).toLocaleString("es-PE")}</p>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {errorCount !== null && (
                                        <span className="text-xs px-2 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200 inline-flex items-center gap-1">
                                            {errorCount > 0 ? <AlertTriangle size={11} /> : <CheckCircle2 size={11} />}
                                            {errorCount} error{errorCount !== 1 ? "es" : ""} de gramática
                                        </span>
                                    )}
                                    {criteriaCount > 0 && (
                                        <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-1">
                                            {criteriaCount} criterio{criteriaCount !== 1 ? "s" : ""} de rúbrica
                                        </span>
                                    )}
                                    {isGraded ? (
                                        <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                                            <CheckCircle2 size={11} /> {s.score}/{assignment.max_score} pts
                                        </span>
                                    ) : (
                                        <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
                                            <Clock size={11} /> Sin calificar
                                        </span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
